import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sspAPI } from '../lib/api';
import {
  FileText,
  Plus,
  Loader2,
  Clock,
  CheckCircle,
  Edit3,
  Trash2,
  Shield,
  AlertCircle,
  ChevronRight
} from 'lucide-react';

const SSPList = () => {
  const navigate = useNavigate();
  const [ssps, setSsps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSSPs();
  }, []);

  const fetchSSPs = async () => {
    try {
      const res = await sspAPI.getAll();
      setSsps(res.data || []);
    } catch (err) {
      console.error('Failed to fetch SSPs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    try {
      const res = await sspAPI.create(createForm);
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '' });
      // Navigate to the new SSP
      navigate(`/ssp/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create SSP');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (sspId) => {
    if (!window.confirm('Are you sure you want to delete this System Security Plan?')) return;
    try {
      await sspAPI.delete(sspId);
      setSsps(ssps.filter(s => s.id !== sspId));
    } catch (err) {
      console.error('Failed to delete SSP:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complete': return 'bg-green-500/20 text-green-400';
      case 'In Progress': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Complete': return <CheckCircle className="w-4 h-4" />;
      case 'In Progress': return <Clock className="w-4 h-4" />;
      default: return <Edit3 className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">System Security Plans</h1>
            <p className="text-slate-400 mt-1">Create and manage NIST 800-171 compliant security plans</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New SSP
          </button>
        </div>

        {/* SSP Cards */}
        {ssps.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <Shield className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No System Security Plans Yet</h2>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Create your first System Security Plan to document how your organization 
              meets NIST 800-171 security requirements.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Create Your First SSP
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {ssps.map((ssp) => (
              <div
                key={ssp.id}
                className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 hover:border-purple-500/50 transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white group-hover:text-purple-300 transition-colors">
                        {ssp.name}
                      </h3>
                      {ssp.description && (
                        <p className="text-slate-400 text-sm mt-1 line-clamp-2">{ssp.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusColor(ssp.status)}`}>
                          {getStatusIcon(ssp.status)}
                          {ssp.status}
                        </span>
                        <span className="text-slate-500 text-sm">
                          {ssp.completionPercentage}% Complete
                        </span>
                        <span className="text-slate-500 text-sm">
                          Modified {formatDate(ssp.lastModified)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDelete(ssp.id)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Delete SSP"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/ssp/${ssp.id}`)}
                      className="flex items-center gap-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      {ssp.status === 'Draft' ? 'Start' : 'Continue'}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all"
                      style={{ width: `${ssp.completionPercentage}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700 shadow-xl">
              <div className="p-6 border-b border-slate-700">
                <h3 className="text-xl font-semibold text-white">Create System Security Plan</h3>
                <p className="text-slate-400 text-sm mt-1">
                  Start a new NIST 800-171 compliant security plan
                </p>
              </div>

              <form onSubmit={handleCreate} className="p-6 space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-slate-300 text-sm mb-1">Plan Name *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="e.g., Corporate IT System SSP"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Brief description of the system this plan covers..."
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateForm({ name: '', description: '' });
                      setError('');
                    }}
                    className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading || !createForm.name.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Create SSP
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SSPList;
